/**
 * ApprovalDetailDialog Component
 *
 * Modal showing full promotion request details:
 * - Header: vertical + config types + environment transition
 * - Change summary
 * - Status timeline (requested -> approved/rejected -> executed)
 * - Action buttons based on status and user permissions
 * - Self-approval prevention
 */

import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Play,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import type { ReactElement, FormEvent } from "react";
import { useState, useCallback } from "react";

import type { PromotionResponse } from "../../hooks/use-approval-api";

// ============================================================================
// Types
// ============================================================================

export interface ApprovalDetailDialogProps {
  /** Whether the dialog is open */
  readonly isOpen: boolean;
  /** Callback to close the dialog */
  readonly onClose: () => void;
  /** Promotion data to display */
  readonly promotion: PromotionResponse;
  /** Current user ID */
  readonly currentUserId: string;
  /** Whether the current user has config:promote capability */
  readonly canPromote: boolean;
  /** Callback to approve the promotion */
  readonly onApprove: (promotionId: string, notes?: string) => Promise<void>;
  /** Callback to reject the promotion */
  readonly onReject: (promotionId: string, reason: string) => Promise<void>;
  /** Callback to execute the promotion */
  readonly onExecute: (promotionId: string) => Promise<void>;
  /** Whether an action is in progress */
  readonly isLoading: boolean;
}

// ============================================================================
// Status Display Config
// ============================================================================

interface StatusInfo {
  readonly label: string;
  readonly color: string;
  readonly icon: typeof CheckCircle;
}

const DEFAULT_STATUS_INFO: StatusInfo = {
  label: "Pending",
  color: "warning",
  icon: Clock,
};

const STATUS_CONFIG: Record<string, StatusInfo> = {
  pending: { label: "Pending", color: "warning", icon: Clock },
  approved: { label: "Approved", color: "success", icon: CheckCircle },
  rejected: { label: "Rejected", color: "error", icon: XCircle },
  executed: { label: "Executed", color: "success", icon: CheckCircle },
  failed: { label: "Failed", color: "error", icon: AlertTriangle },
  cancelled: { label: "Cancelled", color: "muted", icon: XCircle },
};

function getStatusInfo(status: string): StatusInfo {
  return STATUS_CONFIG[status] ?? DEFAULT_STATUS_INFO;
}

// ============================================================================
// Component
// ============================================================================

export function ApprovalDetailDialog({
  isOpen,
  onClose,
  promotion,
  currentUserId,
  canPromote,
  onApprove,
  onReject,
  onExecute,
  isLoading,
}: ApprovalDetailDialogProps): ReactElement | null {
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const isSelfApproval = promotion.requestedBy === currentUserId;
  const isPending = promotion.status === "pending";
  const isApproved = promotion.status === "approved";
  const statusInfo = getStatusInfo(promotion.status);
  const StatusIcon = statusInfo.icon;

  const handleApprove = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setActionError(null);
      try {
        await onApprove(promotion.id, approvalNotes || undefined);
        setApprovalNotes("");
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Approval failed");
      }
    },
    [onApprove, promotion.id, approvalNotes]
  );

  const handleReject = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setActionError(null);
      try {
        await onReject(promotion.id, rejectionReason);
        setRejectionReason("");
        setShowRejectForm(false);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Rejection failed");
      }
    },
    [onReject, promotion.id, rejectionReason]
  );

  const handleExecute = useCallback(async (): Promise<void> => {
    setActionError(null);
    try {
      await onExecute(promotion.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Execution failed");
    }
  }, [onExecute, promotion.id]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      data-testid="approval-detail-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Promotion Request</h2>
            <p className="mt-0.5 text-sm text-foreground-muted">
              {promotion.verticalKey} / {promotion.configTypes.join(", ")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-background-secondary"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-6 px-6 py-4">
          {/* Environment transition */}
          <div className="flex items-center gap-3">
            <span className="rounded-md bg-background-secondary px-3 py-1.5 text-sm font-medium">
              {promotion.sourceEnvironment}
            </span>
            <ArrowRight className="h-4 w-4 text-foreground-muted" />
            <span className="rounded-md bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
              {promotion.targetEnvironment}
            </span>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <StatusIcon
              className={`h-5 w-5 ${
                statusInfo.color === "success"
                  ? "text-success"
                  : statusInfo.color === "error"
                    ? "text-error"
                    : statusInfo.color === "warning"
                      ? "text-warning"
                      : "text-foreground-muted"
              }`}
            />
            <span
              className={`text-sm font-medium ${
                statusInfo.color === "success"
                  ? "text-success"
                  : statusInfo.color === "error"
                    ? "text-error"
                    : statusInfo.color === "warning"
                      ? "text-warning"
                      : "text-foreground-muted"
              }`}
            >
              {statusInfo.label}
            </span>
          </div>

          {/* Change summary */}
          <div>
            <h3 className="mb-1 text-sm font-medium">Change Summary</h3>
            <p className="text-sm text-foreground-muted">{promotion.changeSummary}</p>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="mb-2 text-sm font-medium">Timeline</h3>
            <div className="space-y-3">
              {/* Requested */}
              <TimelineEntry
                label="Requested"
                actor={promotion.requestedBy}
                timestamp={promotion.requestedAt}
              />

              {/* Reviewed */}
              {promotion.reviewedBy && promotion.reviewedAt && (
                <TimelineEntry
                  label={promotion.status === "rejected" ? "Rejected" : "Approved"}
                  actor={promotion.reviewedBy}
                  timestamp={promotion.reviewedAt}
                  notes={promotion.reviewNotes}
                />
              )}

              {/* Executed */}
              {promotion.executedBy && promotion.executedAt && (
                <TimelineEntry
                  label="Executed"
                  actor={promotion.executedBy}
                  timestamp={promotion.executedAt}
                />
              )}
            </div>
          </div>

          {/* Error display */}
          {actionError && (
            <div className="rounded-md bg-error/10 p-3 text-sm text-error">{actionError}</div>
          )}

          {/* Self-approval prevention */}
          {isPending && canPromote && isSelfApproval && (
            <div
              data-testid="self-approval-warning"
              className="rounded-md bg-warning/10 p-3 text-sm text-warning"
            >
              Cannot approve your own request. Another user with promotion capability must review
              this request.
            </div>
          )}

          {/* Approve/Reject actions for pending items */}
          {isPending && canPromote && !isSelfApproval && !showRejectForm && (
            <div className="space-y-3">
              <div>
                <label htmlFor="approval-notes" className="mb-1 block text-sm font-medium">
                  Approval Notes (optional)
                </label>
                <textarea
                  id="approval-notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add optional notes for the requester..."
                  rows={2}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={isLoading}
                />
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="approve-button"
                  onClick={handleApprove}
                  disabled={isLoading}
                  className="btn btn-primary"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  )}
                  Approve
                </button>
                <button
                  data-testid="show-reject-form-button"
                  onClick={() => setShowRejectForm(true)}
                  disabled={isLoading}
                  className="btn btn-secondary text-error"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Reject form */}
          {isPending && canPromote && !isSelfApproval && showRejectForm && (
            <form onSubmit={handleReject} className="space-y-3">
              <div>
                <label htmlFor="rejection-reason" className="mb-1 block text-sm font-medium">
                  Rejection Reason (required)
                </label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this promotion is being rejected..."
                  rows={3}
                  required
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={isLoading}
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  data-testid="reject-button"
                  disabled={isLoading || !rejectionReason.trim()}
                  className="btn btn-primary bg-error hover:bg-error/80"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="mr-2 h-4 w-4" />
                  )}
                  Confirm Rejection
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason("");
                  }}
                  disabled={isLoading}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Execute action for approved items */}
          {isApproved && canPromote && (
            <div className="flex gap-3">
              <button
                data-testid="execute-button"
                onClick={handleExecute}
                disabled={isLoading}
                className="btn btn-primary"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Execute Promotion
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-border px-6 py-4">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Timeline Entry
// ============================================================================

interface TimelineEntryProps {
  readonly label: string;
  readonly actor: string;
  readonly timestamp: string;
  readonly notes?: string | undefined;
}

function TimelineEntry({ label, actor, timestamp, notes }: TimelineEntryProps): ReactElement {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 h-2 w-2 rounded-full bg-foreground-muted" />
      <div>
        <p className="text-sm">
          <span className="font-medium">{label}</span> by{" "}
          <span className="text-foreground-muted">{actor}</span>
        </p>
        <p className="text-xs text-foreground-muted">{new Date(timestamp).toLocaleString()}</p>
        {notes && <p className="mt-1 text-sm text-foreground-muted">{notes}</p>}
      </div>
    </div>
  );
}
